import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { API_BASE } from "../../config";
import Footer from "../homepage/footer";

const Profile = ({ userUID }) => {
  // State to hold user data fetched from the backend
  const [userData, setUserData] = useState(null);
  // Currently edited field
  const [editingField, setEditingField] = useState(null);
  // Data to be sent when editing is saved
  const [updatedData, setUpdatedData] = useState({});
  // Loading state for fetching user data
  const [loading, setLoading] = useState(true);
  // Error state in case fetching/updating fails
  const [error, setError] = useState(null);

  // Fetch user data when component mounts or userUID changes
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userUID) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/api/users/${userUID}`);
        setUserData(response.data);
        setUpdatedData(response.data); // Prepare initial form values
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to load user data.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userUID]);

  // Start editing a specific field
  const handleEdit = (field) => {
    setEditingField(field);
  };

  // Cancel editing, reset data and field
  const handleCancel = () => {
    setEditingField(null);
    setUpdatedData(userData); // Reset to original data
  };

  // Save updated user info to the backend
  const handleSave = async () => {
    try {
      const response = await axios.put(`${API_BASE}/api/users/${userUID}`, updatedData);
      setUserData(response.data); // Update state with saved data
      setEditingField(null); // Stop editing mode
    } catch (error) {
      console.error("Error updating user data:", error);
      setError("Failed to update user data.");
    }
  };

  // Update value of a specific form field
  const handleChange = (field, value) => {
    setUpdatedData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle loading, error or unauthenticated states
  if (loading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (error) return <p className="text-red-500 p-4">{error}</p>;
  if (!userData) return <p className="text-gray-500 p-4">Please sign in to view your profile.</p>;

  // Check if user is an admin
  const isAdmin = userData.role === "admin";

  // Renders a reusable field (editable if allowed)
  const renderField = (label, field, type = "text", editable = true) => (
    <div className="bg-white shadow-md rounded-lg p-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-800">{label}</h2>

        {/* Show edit/save/cancel buttons for editable fields */}
        {editable && !isAdmin && editingField === field ? (
          <>
            <button
              onClick={handleSave}
              className="text-green-600 hover:text-green-800 mr-4"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="text-red-600 hover:text-red-800"
            >
              Cancel
            </button>
          </>
        ) : (
          editable &&
          !isAdmin && (
            <button
              onClick={() => handleEdit(field)}
              className="flex items-center text-teal-600 hover:text-teal-800"
            >
              Edit
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 ml-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M17.414 2.586a2 2 0 00-2.828 0L9.172 8.001a.5.5 0 00-.126.195l-1 4a.5.5 0 00.606.606l4-1a.5.5 0 00.195-.126l5.414-5.414a2 2 0 000-2.828zM11.414 6L14 8.586 12.586 10 10 7.414 11.414 6zM10 9.414L12.586 12 6.172 18.414a1 1 0 01-.414.243l-3 1a1 1 0 01-1.243-1.243l1-3a1 1 0 01.243-.414L10 9.414z" />
              </svg>
            </button>
          )
        )}
      </div>

      {/* Field input or static view */}
      <div className="mt-4">
        {editable && editingField === field && !isAdmin ? (
          field === "level" ? (
            // Level dropdown select
            <select
              value={updatedData[field] || ""}
              onChange={(e) => handleChange(field, e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="Higher">Higher</option>
              <option value="Foundation">Foundation</option>
            </select>
          ) : (
            // Standard input field
            <input
              type={type}
              value={updatedData[field] || ""}
              onChange={(e) => handleChange(field, e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          )
        ) : (
          <>
            <label className="text-sm text-gray-500">{label}</label>
            <p className="text-base font-medium text-gray-700">{userData[field]}</p>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>Profile | MLMaths</title>
        <link rel="canonical" href="https://mlmaths.co.uk/profile" />
        <meta name="description" content="View and update your MLMaths profile, track your quiz history, and monitor your progress." />
      </Helmet>
      <div className="flex-grow container mx-auto py-16 px-6">
        <div className="max-w-3xl mx-auto space-y-8">
          <h1 className="text-3xl font-bold text-teal-500">Profile</h1>

          {/* Render editable profile fields */}
          {renderField("First Name", "first_name")}
          {renderField("Last Name", "last_name")}

          {/* Email field - always read-only */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800">Email Address</h2>
            <div className="mt-4">
              <label className="text-sm text-gray-500">Email Address</label>
              <p className="text-base font-medium text-gray-700">{userData.email_address}</p>
            </div>
          </div>

          {/* Admins see Role, regular users see School Year and Level */}
          {isAdmin
            ? renderField("Role", "role", "text", false) // Admin view only
            : renderField("School Year", "school_year")}

          {!isAdmin && renderField("Level", "level")}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Profile;
